import React from 'react'
import { IColumnType } from '../../../../types/project'

interface ShareModalProps {
  show: boolean
  onClose: () => void
  project: any
  selectedShareColumns: IColumnType
  setSelectedShareColumns: (columns: IColumnType) => void
  handleSelectAllShareColumns: () => void
  handleDeselectAllShareColumns: () => void
  copyShareUrl: () => void
  emailToShare: string
  setEmailToShare: (email: string) => void
  sendShareLinkViaEmail: (email: string) => void
  sharingEmails: string[]
  handleUpdateShareColumns: () => void
}

const columnDisplayNames: { [key: string]: string } = {
  number: 'Lp.',
  stage: 'Etap',
  name: 'Nazwa',
  type: 'Typ',
  price: 'Cena (PLN)',
  quantity: 'Ilość',
  unit: 'Jednostka',
  value: 'Wartość (PLN)',
  url: 'URL',
  image: 'Zdjęcie',
  dimensions: 'Wymiary/Pojemność',
  description: 'Opis',
  status: 'Status',
  deliveryCost: 'Koszt dostawy',
  discountPercentage: 'Procent rabatu',
  marginPercentage: 'Procent marży',
  vatPercentage: 'Procent VAT',
  category: 'Kategoria',
  supplier: 'Dostawca',
  catalogNumber: 'Nr kat.'
}

export const ShareModal: React.FC<ShareModalProps> = ({
  show,
  onClose,
  project,
  selectedShareColumns,
  setSelectedShareColumns,
  handleSelectAllShareColumns,
  handleDeselectAllShareColumns,
  copyShareUrl,
  emailToShare,
  setEmailToShare,
  sendShareLinkViaEmail,
  sharingEmails,
  handleUpdateShareColumns
}) => {
  if (!show) return null

  return (
    <div className='modal show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className='modal-dialog modal-lg' style={{ maxHeight: '90vh', margin: '30px auto' }}>
        <div className='modal-content' style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
          <div className='modal-header'>
            <h5 className='modal-title'>Udostępnij</h5>
            <button type='button' className='close' onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className='modal-body' style={{ overflowY: 'auto', flex: '1 1 auto' }}>
            <div className='row'>
              <div className='col-md-6'>
                <div className='mb-3'>
                  <h6 className='mb-2'>Kolumny do udostępnienia:</h6>
                  <div className='tw-flex tw-justify-between tw-gap-2 tw-my-3'>
                    <button
                      type='button'
                      className={`
                          tw-w-full tw-text-center tw-font-semibold tw-rounded-lg tw-transition-colors tw-duration-150
                          tw-px-4 tw-py-2 tw-text-sm
                          tw-bg-green tw-text-white
                          tw-border tw-border-solid tw-border-green
                          hover:tw-bg-green/80 hover:tw-border-green
                          focus:tw-outline-none focus:tw-bg-green
                        `}
                      onClick={handleSelectAllShareColumns}
                    >
                      Zaznacz wszystkie
                    </button>

                    <button
                      type='button'
                      className={`
                          tw-w-full tw-text-center tw-font-semibold tw-rounded-lg tw-transition-colors tw-duration-150
                          tw-px-4 tw-py-2 tw-text-sm
                          tw-text-green
                          tw-bg-white
                          tw-border tw-border-solid tw-border-green
                          hover:tw-bg-green/10 hover:tw-text-green/80 hover:tw-border-green/80
                          focus:tw-outline-none
                        `}
                      onClick={handleDeselectAllShareColumns}
                    >
                      Odznacz wszystkie
                    </button>
                  </div>

                  <div className='share-columns-list' style={{ maxHeight: '40vh', overflowY: 'auto', paddingRight: '10px' }}>
                    {Object.keys(selectedShareColumns).map(column => (
                      <div className='mb-1' key={column}>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='checkbox'
                            id={`share-column-${column}`}
                            checked={selectedShareColumns[column]}
                            onChange={(e) => setSelectedShareColumns({
                              ...selectedShareColumns,
                              [column]: e.target.checked
                            })}
                          />
                          <label className='form-check-label' htmlFor={`share-column-${column}`}>
                            {columnDisplayNames[column] || column}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className='col-md-6'>
                <div className='mb-3'>
                  <h6 className='mb-2'>Link do projektu:</h6>
                  <div className='input-group'>
                    <input
                      type='text'
                      className='form-control'
                      value={project?.shareUrl ? `${window.location.origin}/projects/shared/${project.shareUrl}` : ''}
                      readOnly
                    />
                    <div className='input-group-append'>
                      <button
                        className={`
                            tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                            tw-text-white
                            tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                          `}
                        onClick={copyShareUrl}
                      >
                        <i className='fa fa-copy' />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <h6 className='mb-2'>Wyślij link:</h6>
                  <div className='form-group'>
                    <div className='input-group'>
                      <input
                        type='email'
                        className='form-control'
                        placeholder='Adres e-mail'
                        value={emailToShare}
                        onChange={(e) => setEmailToShare(e.target.value)}
                      />
                      <div className='input-group-append'>
                        <button
                          className={`
                              tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                              tw-text-white
                              tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                            `}
                          onClick={() => {
                            if (emailToShare.trim()) {
                              sendShareLinkViaEmail(emailToShare)
                            }
                          }}
                          disabled={!emailToShare.trim()}
                        >
                          Wyślij
                        </button>
                      </div>
                    </div>
                  </div>

                  {sharingEmails.length > 0 && (
                    <div className='mt-2'>
                      <small className='text-muted'>Wysłano do:</small>
                      <ul className='list-group list-group-flush'>
                        {sharingEmails.map((email, index) => (
                          <li key={index} className='list-group-item py-1 px-2 d-flex justify-content-between align-items-center'>
                            <small>{email}</small>
                            <span className='badge badge-success badge-pill'>✓</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className='modal-footer'>
            <button
              className={`
                  tw-text-center tw-font-semibold tw-rounded-md tw-shadow-lg tw-transition-colors
                  tw-text-teal
                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-white hover:tw-bg-opacity-90 focus:tw-bg-white
                `}
              onClick={onClose}
            >
              Zamknij
            </button>

            <button
              className={`
                  tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                  tw-text-white
                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                `}
              onClick={handleUpdateShareColumns}
            >
              Zapisz zmiany
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
